import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "modal", "dropzone", "input", "preview", "fileName", "fileSize", 
    "notes", "progress", "progressBar", "progressText", "submitButton"
  ]
  static values = { url: String }
  
  connect() {
    this.selectedFile = null
  }
  
  showModal() {
    this.modalTarget.classList.remove("hidden")
    this.clearFile()
  }
  
  hideModal() {
    this.modalTarget.classList.add("hidden")
    this.clearFile()
  }
  
  openPicker(event) {
    event.stopPropagation()
    this.inputTarget.click()
  }
  
  dragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.add("border-blue-500", "bg-blue-50")
  }
  
  dragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("border-blue-500", "bg-blue-50")
  }
  
  drop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("border-blue-500", "bg-blue-50")
    
    const files = event.dataTransfer.files
    if (files.length > 0) {
      this.selectFile(files[0])
    }
  }
  
  fileSelected(event) {
    const file = event.target.files[0]
    if (file) {
      this.selectFile(file)
    }
  }
  
  selectFile(file) {
    this.selectedFile = file
    
    // Show preview
    this.previewTarget.classList.remove("hidden")
    this.fileNameTarget.textContent = file.name
    this.fileSizeTarget.textContent = this.formatFileSize(file.size)
    
    // Enable submit button
    this.submitButtonTarget.disabled = false
  }
  
  clearFile() {
    this.selectedFile = null
    this.inputTarget.value = ""
    this.previewTarget.classList.add("hidden")
    this.submitButtonTarget.disabled = true
    this.notesTarget.value = ""
    this.progressTarget.classList.add("hidden")
    this.progressBarTarget.style.width = "0%"
  }
  
  async upload() {
    if (!this.selectedFile) {
      alert("Please select a file first")
      return
    }
    
    // Show progress
    this.progressTarget.classList.remove("hidden")
    this.submitButtonTarget.disabled = true
    
    const formData = new FormData()
    formData.append("project_material[file]", this.selectedFile)
    formData.append("project_material[name]", this.selectedFile.name)
    formData.append("project_material[notes]", this.notesTarget.value)
    
    try {
      const csrfToken = document.querySelector("[name='csrf-token']")?.content
      
      const xhr = new XMLHttpRequest()
      
      // Track progress
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100)
          this.progressBarTarget.style.width = `${percent}%`
          this.progressTextTarget.textContent = `${percent}%`
        }
      })
      
      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          // Success - reload page
          window.location.reload()
        } else {
          try {
            const data = JSON.parse(xhr.responseText)
            alert(data.errors?.join(", ") || "Failed to upload file")
          } catch {
            alert("Failed to upload file")
          }
          this.progressTarget.classList.add("hidden")
          this.submitButtonTarget.disabled = false
        }
      })
      
      xhr.addEventListener("error", () => {
        alert("Network error. Please try again.")
        this.progressTarget.classList.add("hidden")
        this.submitButtonTarget.disabled = false
      })
      
      xhr.open("POST", this.urlValue)
      xhr.setRequestHeader("Accept", "application/json")
      xhr.setRequestHeader("X-CSRF-Token", csrfToken)
      xhr.send(formData)
      
    } catch (error) {
      console.error("Upload error:", error)
      alert("Failed to upload file. Please try again.")
      this.progressTarget.classList.add("hidden")
      this.submitButtonTarget.disabled = false
    }
  }
  
  formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }
}
